'use client';

import { useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { getBackgroundClass } from '@workspace/lib';
import { INVISIBLES } from '@workspace/constants';
import type {
  ProductSlideshowSlide,
  ProductSlideshowProps,
  UseProductSlideshowReturn,
} from './product-slideshow.types';

const hasHeading = (slide: ProductSlideshowSlide): boolean => {
  const h = slide.heading;
  if (typeof h === 'string') return h.trim().length > 0;
  if (h && typeof h === 'object' && 'headingText' in h) {
    return Boolean(h.headingText);
  }
  return false;
};

const isValidSlide = (slide: unknown): slide is ProductSlideshowSlide =>
  !!slide && typeof slide === 'object' && hasHeading(slide as ProductSlideshowSlide);

export function useProductSlideshow({ block }: ProductSlideshowProps): UseProductSlideshowReturn {
  const safeBlock = block ?? ({} as ProductSlideshowProps['block']);
  const { backgroundColor, productPosition = 'right' } = safeBlock;

  const slides = Array.isArray(safeBlock.productSlides) ? safeBlock.productSlides.filter(isValidSlide) : [];
  const backgroundClass = getBackgroundClass(backgroundColor);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: slides.length > 1,
    align: 'start',
    containScroll: 'trimSnaps',
    duration: 20,
  });

  const handleNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const handlePrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const cleanProductPosition =
    typeof productPosition === 'string' ? productPosition.replace(INVISIBLES, '').trim().toLowerCase() : 'left';

  const isProductRight = cleanProductPosition === 'right';

  return {
    isActive: !!block && slides.length > 0,
    slides,
    isProductRight,
    backgroundClass,
    emblaRef,
    handleNext,
    handlePrev,
  };
}
